import { BCMSDate, BCMSDefaultModel, BCMSRecord, BCMSStateManagerCRUD, ModalOpen, StateManager, useBCMSContext, useParams, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import FormContainer, { FormButtonContainer, FormRow } from 'components/shared/containers/FormContainer';
import CustomSelect, { CustomSelectItem } from 'components/ui/CustomSelect';
import { UICancelButton, UIPrimaryButton } from 'components/ui/UIButton';
import UIDatePicker from 'components/ui/UIDatePicker ';
import UIRadio from 'components/ui/UIRadio';
import UIExplanationTextInput from 'components/ui/UITextInput/components/UIExplanationTextInput';
import Classes, { Transaction } from 'model';
import React from 'react';
import DisputeProgressWidget from '../widgets/DisputeProgressWidget';
import "./styles.css";

/**
 * @function DisputeMerchandiseReasonView
 * @return {React.Component}
 * @exported true
 * @description The view for stating the reason of disputing a merchandise purchase
 */

export default function DisputeMerchandiseReasonView(props){
  const bcmsContext = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, bcmsContext);
  const navigate = useRoute();
  const params = useParams();
  const { goStep, nextStep, prevStep, state } = useWorkflow() ?? {};
  const paramsTransactionID = useParams().transactionID ?? "0";
  const data = { ...getDefaultData(paramsTransactionID), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,

    update: type => (val, label) => {
      let reason = label.toLowerCase();
      const crud = BCMSStateManagerCRUD.init(state);

      if(type === "wasTheMerchandiseDeliveredElectronically") {
        if(reason === "yes") {
          crud
          .update([ "data", "dispute", "didYouReturnTheMerchandise" ], "")
          .update([ "data", "dispute", "whyDidntYouReturnTheMerchandise" ], "")
          .update([ "data", "dispute", "howDidYouReturnTheMerchandise" ], "")
          .update([ "data", "dispute", "whenWasTheMerchandiseReturned" ], "")
          .update([ "data", "dispute", "howDidYouReturnTheMerchandiseOtherExplanation" ], "")
          .update([ "data", "didSelectWhenWasTheMerchandiseReturned"], false);
          
        }
      }

      if(type === "didYouReturnTheMerchandise") {
        if(reason === "yes") {
          crud
          .update([ "data", "dispute", "whyDidntYouReturnTheMerchandise" ], "");
        } else {
          crud
          .update([ "data", "dispute", "howDidYouReturnTheMerchandise" ], "")
          .update([ "data", "dispute", "whenWasTheMerchandiseReturned" ], "")
          .update([ "data", "dispute", "howDidYouReturnTheMerchandiseOtherExplanation" ], "")
          .update([ "data", "didSelectWhenWasTheMerchandiseReturned"], false);
        }
      }
      
      crud
        .update([ "data", "dispute", type ], reason)
        .update([ "ui" ], ui)
        .submit(setState);
    },


    updateCustomSelect: type => val => {
      let reason = val.toLowerCase();
      
      BCMSStateManagerCRUD.init(state)
        .update([ "data", "dispute", type ], reason)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    // User might select a date from a calendar or type a date.
    updateCustomCalendar: type => val => {
      const crud = BCMSStateManagerCRUD.init(state);

      // Check if the input value is empty if the user is typing a date.
      if(val === "") {
        crud.update([ "data", "dispute", "whenWasTheMerchandiseReturned"], "")
        crud.submit(setState);
        return;
      }

      if(val instanceof BCMSDate) {
        crud.update([ "data", "dispute", type ], val)
      } else {
        let validatedTypeDate = new Date(val);
        let typedDateIsValid = validatedTypeDate.toString() !== "Invalid Date";

        if(typedDateIsValid) {
          validatedTypeDate = BCMSDate.init(validatedTypeDate);
        } else {
          validatedTypeDate = BCMSDate.init(0);
        }

        crud.update([ "data", "dispute", type ], validatedTypeDate)
      }

      crud.submit(setState);
    },

    updateTextInput: type => (arg1, arg2, arg3) => {
      BCMSStateManagerCRUD.init(state)
        .update([ "data", "dispute", type ], arg1)
        .update([ "ui" ], ui)
        .submit(setState);
    },

    next: evt => {
      goStep(4, { data, ui });
    },

    cancel: () => {
      navigate(`/home/transactiondetails/${ params.cardID }/${ params.transactionID }`);
    }
  });

  return(
    <StateManager state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </StateManager>
  );
}

export const getDefaultData = transactionID => {
  const transaction = Transaction.initByID(transactionID);
  const dispute = BCMSDefaultModel.transactionDispute();
  dispute.transaction = transaction;

  return {
    dispute
  };
}

export const getDefaultUI = () => ({
  howDidYouReturnTheMerchandiseCustomSelectIsActive: false,
  whyDidntYouReturnTheMerchandiseCustomSelectIsActive: false
});

function PageContent(props) {
  const { data, next, cancel, update, updateTextInput } = useStateManager();
  const dispute = data.dispute ?? {};
  const nextButtonEnabledCondition1 = dispute.wasTheMerchandiseDeliveredElectronically === "yes"
  && dispute.whyAreYouDissatisfiedWithTheMerchandise.length > 0;

  const nextButtonEnabledCondition2 = dispute.wasTheMerchandiseDeliveredElectronically === "no"
  && dispute.whyAreYouDissatisfiedWithTheMerchandise.length > 0
  && dispute.didYouReturnTheMerchandise === "yes"
  && dispute.howDidYouReturnTheMerchandise.length > 0
  && dispute.whenWasTheMerchandiseReturned !== "";

  const nextButtonEnabledCondition3 = dispute.whyDidntYouReturnTheMerchandise.length > 0
  && dispute.whyAreYouDissatisfiedWithTheMerchandise.length > 0;

  const nextButtonIsEnabled = nextButtonEnabledCondition1 || nextButtonEnabledCondition2 || nextButtonEnabledCondition3;
  const onCancel = evt => cancel();
  const explanationTextInputDataPoint = { dataPoint: "Text_Input_Explanation" };
  const merchandiseDeliveredYesDataPoint = { dataPoint: "Merchandise_Delivered_Yes" };
  const merchandiseDeliveredNoDataPoint = { dataPoint: "Merchandise_Delivered_No" };
  const nextButtonDataPoint = { dataPoint: "Next_Button" };

  return (
    <div className="dispute-merchandise-reason-view">
      <DisputeProgressWidget step="1" />
      <FormContainer>
        <FormRow>
          <p className="form-label">Why are you dissatisfied with the merchandise? *</p>
          <UIExplanationTextInput { ...explanationTextInputDataPoint } maxLength="300" value={ dispute.whyAreYouDissatisfiedWithTheMerchandise } onChange={ updateTextInput("whyAreYouDissatisfiedWithTheMerchandise") } />
        </FormRow>
        <FormRow>
          <p className="form-label">Was the merchandise delivered electronically (e.g., music, software, ebook, etc.)?&nbsp;*</p>
          <UIRadio className="yes-radio" label="Yes" { ...merchandiseDeliveredYesDataPoint  } value={ dispute.wasTheMerchandiseDeliveredElectronically === "yes" } 
            onChange={ update("wasTheMerchandiseDeliveredElectronically") } />
          <UIRadio label="No" { ...merchandiseDeliveredNoDataPoint  } value={ dispute.wasTheMerchandiseDeliveredElectronically === "no" } 
            onChange={ update("wasTheMerchandiseDeliveredElectronically") } />
        </FormRow>

        { dispute.wasTheMerchandiseDeliveredElectronically === "no" &&
        <ReturnMerchandiseWidget />
        }
        { dispute.didYouReturnTheMerchandise === "yes" &&
        <ReturnMerchandiseHowWidget />
        }
        { dispute.didYouReturnTheMerchandise === "no" &&
        <MerchandiseNotReturnedWidget />
        }
        { dispute.howDidYouReturnTheMerchandise.length > 0 &&
        <MerchandiseReturnedDateWidget />
        }
        {/* This is where the widgets that appear after selections of previous questions are asked. */}
        {/* These DO NOT need to be wrapped by the FormRow component as the widgets have the FormRow component already built in  */}
        {/*<ReturnMerchandiseWidget />
        <ReturnMerchandiseHowWidget />
        <MerchandiseNotReturnedWidget />
        <MerchandiseReturnedDateWidget />*/}

        <FormButtonContainer>
          <UIPrimaryButton text="Next" { ...nextButtonDataPoint } className={ nextButtonIsEnabled ? "" : "disabled" } onClick={ next } />
          <ModalOpen id="CancelDisputeTransactionModal" data={ { onCancel: onCancel } }>
            <UICancelButton text="Cancel" />
          </ModalOpen>
        </FormButtonContainer>
      </FormContainer>
    </div>
  );
}

/**
 * @function ReturnMerchandiseWidget
 * @return {React.Component}
 * @exported false
 * @description The widget that shows the question about returning merchandise
 */

function ReturnMerchandiseWidget(props) {
  const { data, update } = useStateManager();
  const dispute = data.dispute ?? {};
  const yesDataPoint = { dataPoint: "Merchandise_Returned_Yes" };
  const noDataPoint = { dataPoint: "Merchandise_Returned_No" };

  return(
    <div className="return-merchandise-widget">
      <FormRow>
        <p className="form-label">Did you return the merchandise? *</p>
        <UIRadio label="Yes" { ...yesDataPoint } value={ dispute.didYouReturnTheMerchandise === "yes" } 
            onChange={ update("didYouReturnTheMerchandise") } />
        <UIRadio label="No" { ...noDataPoint } value={ dispute.didYouReturnTheMerchandise === "no" } 
            onChange={ update("didYouReturnTheMerchandise") } />
      </FormRow>
    </div>
  );
}

/**
 * @function ReturnMerchandiseHowWidget
 * @return {React.Component}
 * @exported false
 * @description The widget that asks how the merchandise was returned
 */

function ReturnMerchandiseHowWidget(props){
  const { data, ui, next, cancel, update, updateCustomSelect, updateTextInput } = useStateManager();
  const dispute = data.dispute ?? {};
  const selectDataPoint = { "data-point": "Returned_Merchandise_Reason_Select" };
  const option1DataPoint =  { "data-point": "Returned_Merchandise_Reason_Option_1" };

  return(
    <div className="return-merchandise-how-widget">
      <FormRow>
        <p className="form-label">How did you return the merchandise? *</p>
        <CustomSelect isActive={ ui.howDidYouReturnTheMerchandiseCustomSelectIsActive } { ...selectDataPoint } label="Select" value={ dispute.howDidYouReturnTheMerchandise } onChange={ updateCustomSelect("howDidYouReturnTheMerchandise") }>
          <CustomSelectItem value="in-person">In Person</CustomSelectItem>
          <CustomSelectItem value="fedex" { ...option1DataPoint }>FedEx</CustomSelectItem>
          <CustomSelectItem value="usps">USPS</CustomSelectItem>
          <CustomSelectItem value="ups">UPS</CustomSelectItem>
          <CustomSelectItem value="other">Other</CustomSelectItem>
        </CustomSelect>
      </FormRow>
      { dispute.howDidYouReturnTheMerchandise === "other" &&
      <FormRow>
        <UIExplanationTextInput maxLength="300" value={ dispute.howDidYouReturnTheMerchandiseOtherExplanation }
        onChange={ updateTextInput("howDidYouReturnTheMerchandiseOtherExplanation") } />
      </FormRow>
      }
    </div>
  );
}

/**
 * @function MerchandiseNotReturnedWidget
 * @return {React.Component}
 * @exported false
 * @description Widget that asks why merchandise was not returned
 */

function MerchandiseNotReturnedWidget(props) {
  const { data, ui, next, cancel, update, updateCustomSelect, updateTextInput } = useStateManager();
  const dispute = data.dispute ?? {};
  const selectDataPoint = { "data-point": "Did_Not_Return_Merchandise_Reason_Select" };
  const option0DataPoint =  { "data-point": "Did_Not_Return_Merchandise_Reason_Option_0" };
  
  return(
    <div className="merchandise-not-returned-widget">
      <FormRow>
        <p className="form-label">Why didn't you return the merchandise? *</p>
        <CustomSelect isActive={ ui.whyDidntYouReturnTheMerchandiseCustomSelectIsActive } label="Select" { ...selectDataPoint } value={ dispute.whyDidntYouReturnTheMerchandise } onChange={ updateCustomSelect("whyDidntYouReturnTheMerchandise") }>
          <CustomSelectItem value="informed-not-to-return" { ...option0DataPoint }>Merchant informed the cardholder not to return the merchandise</CustomSelectItem>
          <CustomSelectItem value="no-return-auth">Merchant refused to provide a return authorization</CustomSelectItem>
          <CustomSelectItem value="not-accept-return">Merchant would not accept a return</CustomSelectItem>
        </CustomSelect>
      </FormRow>
    </div>
  );
}

/**
 * @function MerchandiseReturnedDateWidget
 * @return {React.Component}
 * @exported false
 * @description Widget that asks when the merchandise was returned
 */

function MerchandiseReturnedDateWidget(props){
  const { data, next, cancel, update, updateCustomCalendar } = useStateManager();
  const dispute = data.dispute ?? {};
  const datePickerDataPoint = { "data-point": "Merchandise_Returned_Date" };
  const subTextDataPoint = { "data-point": "Merchandise_Returned_Date_Sub_Text" };

  return(
    <div className="merchandise-returned-date-widget">
      <FormRow>
        <p className="form-label">When was the merchandise returned? *</p>
        <UIDatePicker { ...datePickerDataPoint } dateLabel="Date (MMM DD, YYYY)" allowPast={ true } allowFuture={ false } value={ dispute.whenWasTheMerchandiseReturned } onChange={ updateCustomCalendar("whenWasTheMerchandiseReturned") } />
        <p className="sub-text return-date-helper-text" { ...subTextDataPoint }>Enter a guess if necessary</p>
      </FormRow>
    </div>
  );
}

